import React, {useEffect, useMemo, useRef, useState} from 'react';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../firebaseConfig';
import './PublicRecordComplianceRemoval.css';

export const PERMANENT_REMOVAL_CONFIRMATION = 'PERMANENTLY_REMOVE_PUBLIC_RECORDS';

export const REMOVAL_REASONS = Object.freeze([
  {value: 'LEGAL_EXPUNGEMENT', label: 'Legal Expungement', policy: 'EVENT_PERMANENT'},
  {value: 'LEGAL_ORDER', label: 'Legal Order', policy: 'EVENT_PERMANENT'},
  {value: 'SOURCE_REMOVAL', label: 'Source Removal', policy: 'EVENT_PERMANENT'},
  {value: 'SOURCE_CORRECTION', label: 'Source Correction', policy: 'REVIEW_REQUIRED'},
  {value: 'ERRONEOUS_IDENTITY', label: 'Erroneous Identity', policy: 'REVIEW_REQUIRED'},
  {value: 'ERRONEOUS_PHOTO', label: 'Erroneous Photo', policy: 'REVIEW_REQUIRED'},
  {value: 'DUPLICATE', label: 'Duplicate', policy: 'NONE'},
  {value: 'ADMIN_CLEANUP', label: 'Administrative Cleanup', policy: 'NONE'},
]);

const SOURCE_OPTIONS = Object.freeze([
  {value: '', label: 'All supported sources'},
  {value: 'nc_wake_bfs', label: 'Wake County'},
  {value: 'nc_mecklenburg_sheriff', label: 'Mecklenburg County'},
  {value: 'nc_harnett_sheriff', label: 'Harnett County'},
]);

const PHASES = Object.freeze([
  {value: 'BARRIER', label: 'Barrier established'},
  {value: 'AWS', label: 'AWS biometrics removal'},
  {value: 'STORAGE', label: 'Storage destruction'},
  {value: 'FIRESTORE', label: 'Firestore purge'},
  {value: 'RECONCILE', label: 'Identity reconciliation'},
  {value: 'VERIFY', label: 'Final verification'},
  {value: 'AUDIT', label: 'Destruction audit'},
  {value: 'COMPLETE', label: 'Complete'},
]);

const TERMINAL_STATUSES = new Set(['COMPLETED', 'PERMANENT_FAILURE']);
const EMPTY_CRITERIA = Object.freeze({canonicalPersonId: '', dob: '', firstName: '', lastName: '',
  middleName: '', photoId: '', recordDate: '', sourceId: '', sourceIdentityId: '',
  sourceRecordId: '', sourceRecordKey: ''});

const cleanError = (error, fallback) => {
  const code = String(error?.code || '').replace(/^functions\//, '');
  if (code === 'permission-denied') return 'This account is not authorized for Compliance Removal.';
  if (code === 'unauthenticated') return 'Your admin session expired. Sign in again.';
  if (['unavailable', 'network-request-failed'].includes(code)) return 'The compliance service is temporarily unavailable.';
  return error?.details?.publicRecordCode || error?.message || fallback;
};

const dateDisplay = (value, includeTime = false) => {
  if (!value) return 'Unavailable';
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, includeTime ? {dateStyle: 'medium', timeStyle: 'short'} :
    {dateStyle: 'medium'}).format(parsed);
};

const fullName = (record) => record?.fullName || [record?.identity?.firstName,
  record?.identity?.middleName, record?.identity?.lastName, record?.identity?.suffix]
    .filter(Boolean).join(' ') || 'Name unavailable';

export const nameComparisonText = (comparison = {}) => {
  if (comparison.status === 'EXACT') return 'Name: Exact';
  if (comparison.status === 'COMPATIBLE_MIDDLE_INITIAL') return 'Name: Compatible — middle initial';
  if (comparison.status === 'COMPATIBLE_MISSING') {
    const components = (comparison.components || []).join(', ');
    return `Name: Compatible — ${components || 'data'} unavailable`;
  }
  if (comparison.status === 'CONFLICT') {
    return `Name: Conflict — ${(comparison.components || []).join(', ') || 'identity'}`;
  }
  return 'Name: Comparison unavailable';
};

export const dobComparisonText = (comparison = {}) => {
  if (comparison.status === 'EXACT') return 'DOB: Exact';
  if (comparison.status === 'CONFLICT') return `DOB: Conflict — ${dateDisplay(comparison.confirmed)} / ${dateDisplay(comparison.candidate)}`;
  if (comparison.status === 'CANDIDATE_ONLY') return `DOB: Candidate only — ${dateDisplay(comparison.candidate)}`;
  if (comparison.status === 'CONFIRMED_ONLY') return `DOB: Confirmed only — ${dateDisplay(comparison.confirmed)}`;
  return 'DOB: Unavailable';
};

const reasonLabel = (value) => REMOVAL_REASONS.find((reason) => reason.value === value)?.label || value;
const policyLabel = (value) => ({EVENT_PERMANENT: 'Permanent event suppression',
  SOURCE_PERSON_PERMANENT: 'Permanent source-person suppression',
  REVIEW_REQUIRED: 'Review required before re-import', NONE: 'No re-import suppression'}[value] || value);

const sourceCounts = (records) => records.reduce((counts, record) => {
  const label = record.sourceLabel || record.sourceId;
  counts[label] = (counts[label] || 0) + 1;
  return counts;
}, {});

const EventCard = ({record, action, selected = false}) => <article className={`compliance-event-card${selected ? ' selected' : ''}`}>
  <div className="compliance-mugshot">
    {record.photoUrl ? <img src={record.photoUrl} alt={`Mugshot for ${fullName(record)}`} /> :
      <span>Photo unavailable</span>}
  </div>
  <div className="compliance-event-details">
    <h4>{fullName(record)}</h4>
    <dl>
      <div><dt>DOB</dt><dd>{record.dob ? dateDisplay(record.dob) : 'Unavailable'}</dd></div>
      <div><dt>Source</dt><dd>{record.sourceLabel || record.sourceId}</dd></div>
      <div><dt>Record date</dt><dd>{dateDisplay(record.recordDate)}</dd></div>
      <div><dt>Source record</dt><dd>{record.maskedSourceRecordIdentifier || 'Unavailable'}</dd></div>
      <div><dt>Status</dt><dd>{record.status || 'Unavailable'}</dd></div>
      <div><dt>Related current records</dt><dd>{record.relatedCurrentRecordCount || 0}</dd></div>
    </dl>
    {action}
  </div>
</article>;

const CandidateCard = ({candidate, checked, onChange, onDismiss}) => <article className={`compliance-candidate-card${checked ? ' selected' : ''}`}>
  <div className="compliance-mugshot">
    {candidate.photoUrl ? <img src={candidate.photoUrl} alt={`Candidate mugshot for ${fullName(candidate)}`} /> :
      <span>Photo unavailable</span>}
  </div>
  <div className="compliance-candidate-body">
    <h4>{fullName(candidate)}</h4>
    <p className={candidate.nameComparison?.status === 'CONFLICT' ? 'comparison-conflict' : ''}>
      {nameComparisonText(candidate.nameComparison)}
    </p>
    <p><strong>Face similarity:</strong> {Number(candidate.faceSimilarity).toFixed(1)}%</p>
    <p className={candidate.dobComparison?.status === 'CONFLICT' ? 'comparison-conflict' : ''}>
      {dobComparisonText(candidate.dobComparison)}
    </p>
    <p><strong>Source:</strong> {candidate.sourceLabel || candidate.sourceId}</p>
    <p><strong>Record date:</strong> {dateDisplay(candidate.recordDate)}</p>
    <div className="compliance-candidate-actions">
      <label><input type="checkbox" checked={checked} onChange={onChange} /> Include in Removal</label>
      <button type="button" className="secondary danger-outline" onClick={onDismiss}>Not Same Person</button>
    </div>
  </div>
</article>;

const Summary = ({records, onReview}) => {
  const counts = sourceCounts(records);
  const sourceTotal = Object.keys(counts).length;
  const summary = `${records.length} record${records.length === 1 ? '' : 's'} across ${sourceTotal} ` +
    `${sourceTotal === 1 ? 'source/county' : 'sources/counties'}`;
  return <section className="compliance-removal-set" aria-labelledby="removal-set-heading">
    <h3 id="removal-set-heading">Selected for removal</h3>
    <p><strong>{summary}</strong></p>
    <ul>{Object.entries(counts).map(([label, count]) => <li key={label}>{label} — {count}</li>)}</ul>
    <button type="button" className="primary" onClick={onReview}>Review Removal</button>
  </section>;
};

const PublicRecordComplianceRemoval = () => {
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [confirmed, setConfirmed] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [removalSet, setRemovalSet] = useState({});
  const [reasonCategory, setReasonCategory] = useState(REMOVAL_REASONS[0].value);
  const [reimportPolicy, setReimportPolicy] = useState(REMOVAL_REASONS[0].policy);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const pollingRef = useRef(null);

  const selectedRecords = useMemo(() => Object.values(removalSet), [removalSet]);
  const supportsPersonSuppression = selectedRecords.length > 0 && selectedRecords.every((record) =>
    record.supportsSourcePersonSuppression === true);
  const targetScope = selectedRecords.length === 1 ? 'SOURCE_RECORD' : 'MULTIPLE_SOURCE_RECORDS';

  const call = async (payload) => (await httpsCallable(functions,
    'publicRecordComplianceRemoval')(payload)).data;

  const resetReview = () => {
    setPreview(null);
    setAcknowledged(false);
    setError('');
  };

  const updateCriteria = (event) => {
    setCriteria((current) => ({...current, [event.target.name]: event.target.value}));
    setSearchAttempted(false);
  };

  const search = async (event) => {
    event.preventDefault();
    setBusy('search');
    setError('');
    try {
      const response = await call({action: 'search', criteria});
      setSearchResults(response?.results || []);
      setSearchAttempted(true);
    } catch (caught) {
      setSearchAttempted(true);
      setError(cleanError(caught, 'Could not search Public Records events.'));
    } finally {
      setBusy('');
    }
  };

  const selectConfirmed = (record) => {
    setConfirmed(record);
    setCandidates([]);
    setDismissed([]);
    resetReview();
  };

  const addRecord = (record) => {
    setRemovalSet((current) => ({...current, [record.sourceRecordId]: record}));
    resetReview();
  };

  const toggleRecord = (record) => {
    setRemovalSet((current) => {
      const next = {...current};
      if (next[record.sourceRecordId]) delete next[record.sourceRecordId];
      else next[record.sourceRecordId] = record;
      return next;
    });
    resetReview();
  };

  const discover = async () => {
    if (!confirmed) return;
    setBusy('discover');
    setError('');
    try {
      const response = await call({action: 'discover', sourceRecordId: confirmed.sourceRecordId});
      setCandidates([...(response?.candidates || [])].sort((left, right) =>
        right.faceSimilarity - left.faceSimilarity));
      setDismissed([]);
    } catch (caught) {
      setError(cleanError(caught, 'Could not search for other Public Records.'));
    } finally {
      setBusy('');
    }
  };

  const dismissCandidate = (candidate) => {
    setCandidates((current) => current.filter((item) => item.sourceRecordId !== candidate.sourceRecordId));
    setDismissed((current) => [...current, candidate]);
    setRemovalSet((current) => {
      const next = {...current};
      delete next[candidate.sourceRecordId];
      return next;
    });
    resetReview();
  };

  const restoreCandidate = (candidate) => {
    setDismissed((current) => current.filter((item) => item.sourceRecordId !== candidate.sourceRecordId));
    setCandidates((current) => [...current, candidate].sort((left, right) =>
      right.faceSimilarity - left.faceSimilarity));
  };

  const changeReason = (event) => {
    const value = event.target.value;
    setReasonCategory(value);
    setReimportPolicy(REMOVAL_REASONS.find((reason) => reason.value === value)?.policy || 'NONE');
    resetReview();
  };

  const reviewRemoval = async () => {
    if (!selectedRecords.length) return;
    setBusy('preview');
    setError('');
    try {
      const response = await call({action: 'preview', reasonCategory, reimportPolicy,
        sourceRecordIds: selectedRecords.map((record) => record.sourceRecordId), targetScope});
      setPreview(response);
      setAcknowledged(false);
    } catch (caught) {
      setError(cleanError(caught, 'Could not preview the removal.'));
    } finally {
      setBusy('');
    }
  };

  const submitRemoval = async () => {
    if (!preview || !acknowledged) return;
    setBusy('request');
    setError('');
    try {
      const response = await call({action: 'request', confirmationToken: preview.confirmationToken,
        confirmationValue: PERMANENT_REMOVAL_CONFIRMATION, reasonCategory, reimportPolicy,
        sourceRecordIds: selectedRecords.map((record) => record.sourceRecordId), targetScope});
      setJobId(response.jobId);
      setJobStatus({jobId: response.jobId, phase: 'BARRIER', status: response.status || 'PENDING'});
      setPreview(null);
    } catch (caught) {
      setError(cleanError(caught, 'Could not create the removal job.'));
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (!jobId || TERMINAL_STATUSES.has(jobStatus?.status)) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await call({action: 'status', jobId});
        if (!cancelled && response) setJobStatus(response);
      } catch (caught) {
        if (!cancelled) setError(cleanError(caught, 'Could not refresh removal status.'));
      }
    };
    refresh();
    pollingRef.current = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
    // Status changes intentionally stop polling on terminal states.
  }, [jobId, jobStatus?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    setBusy('history');
    setError('');
    try {
      const response = await call({action: 'history', limit: 50});
      setHistory(response?.items || []);
      setHistoryLoaded(true);
    } catch (caught) {
      setError(cleanError(caught, 'Could not load removal history.'));
    } finally {
      setBusy('');
    }
  };

  const startAnother = () => {
    setCriteria(EMPTY_CRITERIA);
    setSearchAttempted(false);
    setSearchResults([]);
    setConfirmed(null);
    setCandidates([]);
    setDismissed([]);
    setRemovalSet({});
    setPreview(null);
    setAcknowledged(false);
    setJobId('');
    setJobStatus(null);
    setReasonCategory(REMOVAL_REASONS[0].value);
    setReimportPolicy(REMOVAL_REASONS[0].policy);
    setError('');
  };

  const totals = preview?.targets?.reduce((sum, target) => ({observations: sum.observations +
    (target.observationCount || 0), photos: sum.photos + (target.retainedPhotoCount || 0),
  normalized: sum.normalized + (target.normalizedFaceImageCount || 0), templates: sum.templates +
    (target.templateCount || 0), projections: sum.projections + (target.nameProjectionCount || 0),
  remaining: sum.remaining + (target.relatedCurrentRecords?.length || 0)}),
  {observations: 0, photos: 0, normalized: 0, templates: 0, projections: 0, remaining: 0});

  if (jobId) {
    const completed = jobStatus?.status === 'COMPLETED';
    const attention = jobStatus?.status === 'PERMANENT_FAILURE';
    const retrying = jobStatus?.status === 'RETRYABLE_FAILURE';
    const currentPhase = PHASES.findIndex((phase) => phase.value === jobStatus?.phase);
    return <section className="compliance-removal-panel compliance-status-view" aria-labelledby="compliance-heading">
      <header><p className="compliance-eyebrow">Public Records</p><h2 id="compliance-heading">Compliance Removal</h2></header>
      <div className={`compliance-state-banner ${completed ? 'complete' : attention ? 'attention' : retrying ? 'retrying' : ''}`} role="status">
        <strong>{completed ? 'Complete' : attention ? 'Action Required / Operator Attention' : retrying ? 'Retrying' : 'Processing'}</strong>
        <span>Removal {jobId}</span>
      </div>
      <ol className="compliance-phases">
        {PHASES.map((phase, index) => <li key={phase.value} className={index < currentPhase || completed ? 'done' :
          index === currentPhase ? 'current' : ''}>{phase.label}</li>)}
      </ol>
      {retrying && <p className="compliance-warning">The search barrier remains active. The worker will retry this phase{jobStatus.nextAttemptAt ? ` after ${dateDisplay(jobStatus.nextAttemptAt, true)}` : ''}.</p>}
      {jobStatus?.lastErrorCode && <p className="compliance-error" role="alert">Operator status: {jobStatus.lastErrorCode}</p>}
      {completed && <section className="compliance-completion" aria-label="Destruction summary">
        <h3>Removal {jobId}</h3>
        <dl>
          <div><dt>Search blocked</dt><dd>✓</dd></div>
          <div><dt>AWS templates removed</dt><dd>✓</dd></div>
          <div><dt>AWS absence verified</dt><dd>{jobStatus.verification?.awsVerifiedAbsent ? '✓' : 'Pending'}</dd></div>
          <div><dt>Storage objects removed</dt><dd>{jobStatus.verification?.storageVerifiedAbsent ? '✓' : 'Pending'}</dd></div>
          <div><dt>Firestore references purged</dt><dd>{jobStatus.verification?.firestoreVerifiedAbsent ? '✓' : 'Pending'}</dd></div>
          <div><dt>Suppression installed</dt><dd>{reimportPolicy === 'NONE' ? 'Not requested' : '✓'}</dd></div>
          <div><dt>Final verification</dt><dd>✓</dd></div>
        </dl>
        <p><strong>Completed:</strong> {dateDisplay(jobStatus.completedAt, true)}</p>
        <p><strong>Reason:</strong> {reasonLabel(reasonCategory)}</p>
        <p><strong>Records removed:</strong> {selectedRecords.length}</p>
        <p><strong>Sources:</strong> {Object.keys(sourceCounts(selectedRecords)).join(', ')}</p>
        <button type="button" className="primary" onClick={startAnother}>Start Another Review</button>
      </section>}
    </section>;
  }

  return <section className="compliance-removal-panel" aria-labelledby="compliance-heading">
    <header className="compliance-heading">
      <div><p className="compliance-eyebrow">Public Records</p><h2 id="compliance-heading">Compliance Removal</h2></div>
      <span className="compliance-permanent-label">Permanent destruction workflow</span>
    </header>
    <p className="compliance-intro">Locate and verify an explicit source event. Search, selection, and review are read-only. Destruction begins only after final authorization.</p>
    {error && <p className="compliance-error" role="alert">{error}</p>}

    <form className="compliance-search" onSubmit={search} aria-label="Locate a Public Records event">
      <h3>Locate Specific Event</h3>
      <div className="compliance-form-grid">
        <label>Source / county<input name="sourceId" list="compliance-source-options" value={criteria.sourceId}
          onChange={updateCriteria} placeholder="All supported sources" />
        <datalist id="compliance-source-options">{SOURCE_OPTIONS.filter((source) => source.value)
          .map((source) => <option key={source.value} value={source.value}>{source.label}</option>)}</datalist></label>
        <label>First name<input name="firstName" value={criteria.firstName} onChange={updateCriteria} /></label>
        <label>Middle name / initial<input name="middleName" value={criteria.middleName} onChange={updateCriteria} /></label>
        <label>Last name<input name="lastName" value={criteria.lastName} onChange={updateCriteria} /></label>
        <label>DOB<input type="date" name="dob" value={criteria.dob} onChange={updateCriteria} /></label>
        <label>Record / arrest date<input type="date" name="recordDate" value={criteria.recordDate} onChange={updateCriteria} /></label>
        <label>Source record identifier<input name="sourceRecordKey" value={criteria.sourceRecordKey} onChange={updateCriteria} placeholder="Wake O_ID, Mecklenburg ArrestNumber, or Harnett BookingID" /></label>
        <label>BlueWire sourceRecordId<input name="sourceRecordId" value={criteria.sourceRecordId} onChange={updateCriteria} /></label>
      </div>
      <details className="compliance-advanced-locator"><summary>Additional BlueWire identifiers</summary>
        <div className="compliance-form-grid">
          <label>sourceIdentityId<input name="sourceIdentityId" value={criteria.sourceIdentityId} onChange={updateCriteria} /></label>
          <label>canonicalPersonId<input name="canonicalPersonId" value={criteria.canonicalPersonId} onChange={updateCriteria} /></label>
          <label>photoId<input name="photoId" value={criteria.photoId} onChange={updateCriteria} /></label>
        </div>
      </details>
      <button type="submit" className="primary" disabled={Boolean(busy)}>{busy === 'search' ? 'Searching…' : 'Search Records'}</button>
    </form>

    {searchResults.length > 0 && <section className="compliance-results" aria-labelledby="search-results-heading">
      <h3 id="search-results-heading">Search Results</h3>
      <div className="compliance-event-grid">{searchResults.map((record) => <EventCard key={record.sourceRecordId} record={record}
        action={<button type="button" className="primary" onClick={() => selectConfirmed(record)}>Select This Record</button>} />)}</div>
    </section>}
    {!busy && searchAttempted && searchResults.length === 0 &&
      <p className="compliance-empty">No matching event is currently displayed.</p>}

    {confirmed && <section className="compliance-confirmed" aria-labelledby="confirmed-event-heading">
      <h3 id="confirmed-event-heading">Confirmed Event</h3>
      <EventCard record={confirmed} selected action={<div className="compliance-confirmed-actions">
        <button type="button" className="primary danger" onClick={() => addRecord(confirmed)}>Remove This Event</button>
        <button type="button" className="secondary" onClick={discover} disabled={busy === 'discover'}>
          {busy === 'discover' ? 'Searching Public Records…' : 'Find Other Records for This Person'}
        </button>
      </div>} />
    </section>}

    {candidates.length > 0 && <section className="compliance-discovery" aria-labelledby="candidate-heading">
      <h3 id="candidate-heading">Possible Other Records</h3>
      <p>Face similarity is a discovery aid only. Review each event manually; no candidate is included automatically.</p>
      <div className="compliance-candidate-grid">{candidates.map((candidate) => <CandidateCard key={candidate.sourceRecordId}
        candidate={candidate} checked={Boolean(removalSet[candidate.sourceRecordId])}
        onChange={() => toggleRecord(candidate)} onDismiss={() => dismissCandidate(candidate)} />)}</div>
    </section>}
    {dismissed.length > 0 && <section className="compliance-dismissed"><h4>Dismissed in this review</h4>
      <ul>{dismissed.map((candidate) => <li key={candidate.sourceRecordId}>{fullName(candidate)} — {candidate.sourceLabel}
        <button type="button" onClick={() => restoreCandidate(candidate)}>Undo</button></li>)}</ul></section>}

    {selectedRecords.length > 0 && !preview && <>
      <section className="compliance-policy" aria-labelledby="removal-policy-heading">
        <h3 id="removal-policy-heading">Removal Policy</h3>
        <div className="compliance-form-grid">
          <label>Reason<select value={reasonCategory} onChange={changeReason}>
            {REMOVAL_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
          </select></label>
          <label>Re-import policy<select value={reimportPolicy} onChange={(event) => {setReimportPolicy(event.target.value); resetReview();}}>
            <option value="EVENT_PERMANENT">Permanent event suppression</option>
            <option value="REVIEW_REQUIRED">Review required before re-import</option>
            <option value="NONE">No re-import suppression</option>
          </select></label>
        </div>
        <button type="button" className="text-button" aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((open) => !open)}>Advanced suppression options</button>
        {advancedOpen && <div className="compliance-advanced-policy">
          <label><input type="radio" name="advanced-policy" checked={reimportPolicy === 'SOURCE_PERSON_PERMANENT'}
            disabled={!supportsPersonSuppression} onChange={() => {setReimportPolicy('SOURCE_PERSON_PERMANENT'); resetReview();}} />
            Permanently suppress stable source-person identifier</label>
          {!supportsPersonSuppression && <p>Unavailable: every selected source must expose a stable source-person key. Wake County never supports this option.</p>}
        </div>}
      </section>
      <Summary records={selectedRecords} onReview={reviewRemoval} />
    </>}

    {preview && <section className="compliance-final-review" aria-labelledby="review-removal-heading">
      <h3 id="review-removal-heading">Review Removal</h3>
      <div className="compliance-preview-grid">{preview.targets.map((target) => {
        const selected = removalSet[target.sourceRecordId] || target;
        return <article key={target.sourceRecordId}><h4>{fullName(selected)}</h4>
          <p>{selected.sourceLabel || target.sourceId} — {dateDisplay(target.recordDate)}</p>
          <p>Source record: {target.maskedSourceRecordIdentifier}</p>
          <dl><div><dt>Observations</dt><dd>{target.observationCount}</dd></div>
            <div><dt>Retained photos</dt><dd>{target.retainedPhotoCount}</dd></div>
            <div><dt>AWS templates</dt><dd>{target.templateCount}</dd></div>
            <div><dt>Name projections</dt><dd>{target.nameProjectionCount}</dd></div>
            <div><dt>Related records remaining</dt><dd>{target.relatedCurrentRecords?.length || 0}</dd></div></dl>
        </article>;
      })}</div>
      <section className="compliance-destruction-totals"><h4>This removal will destroy</h4>
        <ul><li>{preview.targets.length} source records</li><li>{totals.observations} observations</li>
          <li>{totals.photos} retained source photos</li><li>{totals.normalized} normalized face images</li>
          <li>{totals.templates} AWS biometric templates</li><li>{totals.projections} name-search projections</li></ul>
        <p><strong>Other lawful records remaining:</strong> {totals.remaining}</p>
        <p><strong>Suppression:</strong> {policyLabel(reimportPolicy)} ({preview.proposedSuppressionBehavior?.count || 0} suppression records)</p>
      </section>
      {preview.warnings?.length > 0 && <div className="compliance-warning"><strong>Warnings</strong>
        <ul>{[...new Set(preview.warnings)].map((warning) => <li key={warning}>{warning.replaceAll('_', ' ').toLowerCase()}</li>)}</ul></div>}
      <div className="compliance-authorization">
        <p><strong>This action permanently destroys the selected Public Records data.</strong></p>
        <label><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
          I confirm that I am authorized to permanently remove the selected Public Records data.</label>
        <div className="compliance-final-actions"><button type="button" className="secondary" onClick={() => {setPreview(null); setAcknowledged(false);}}>Back</button>
          <button type="button" className="primary danger" disabled={!acknowledged || busy === 'request'} onClick={submitRemoval}>
            {busy === 'request' ? 'Creating Removal Job…' : 'Permanently Remove Selected Records'}</button></div>
      </div>
    </section>}

    <section className="compliance-history" aria-labelledby="removal-history-heading">
      <div><h3 id="removal-history-heading">Removal History</h3><p>Minimal technical destruction audit only; no retained names, DOBs, mugshots, provider IDs, or storage paths.</p></div>
      <button type="button" className="secondary" onClick={loadHistory} disabled={busy === 'history'}>
        {busy === 'history' ? 'Loading…' : historyLoaded ? 'Refresh History' : 'Load Removal History'}</button>
      {historyLoaded && (history.length ? <div className="compliance-history-table" role="table" aria-label="Compliance removal history">
        {history.map((item) => <article role="row" key={item.deletionJobId}>
          <strong>{item.deletionJobId}</strong><span>{dateDisplay(item.completedAt, true)}</span>
          <span>{(item.sourceIds || []).join(', ')}</span><span>{reasonLabel(item.reasonCategory)}</span>
          <span>{item.targetScope}</span><span>{policyLabel(item.reimportPolicy)}</span>
          <span>Requested by {item.requestedByAdminUid || 'Unavailable'}</span><span>{item.finalStatus}</span>
        </article>)}</div> : <p>No completed removals were returned.</p>)}
    </section>
  </section>;
};

export default PublicRecordComplianceRemoval;
